import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { GIFT_ICONS } from "./GiftModal";

function Stat({ label, value, testid, tone = "text-[#FBF8F3]" }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-[#9E978E]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function PopularityTable({ rows }) {
  return (
    <table className="w-full text-sm" data-testid="gift-report-popularity">
      <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#9E978E]">
        <th className="py-2">Gift</th><th>Sent</th><th>Credits spent</th><th>To recipients</th><th>DateBid retained</th><th>Refunded</th><th>Current price</th>
      </tr></thead>
      <tbody>
        {rows.map((g, i) => {
          const Icon = GIFT_ICONS[g.icon] || GIFT_ICONS.gift;
          return (
            <tr key={g.gift_id} className="border-t border-white/8 text-[#D8D2C9]" data-testid={`gift-report-row-${g.gift_id}`}>
              <td className="py-2 flex items-center gap-2"><span className="text-[#9E978E] w-5">#{i + 1}</span><Icon size={15} className="text-[#D4AF37]" />{g.label}{!g.active && <span className="text-[10px] text-[#9E978E]">(inactive)</span>}</td>
              <td>{g.count}</td><td>{g.credits_spent}</td><td>{g.recipient_credits}</td><td>{g.datebid_retained_credits}</td><td>{g.refunded_credits}</td>
              <td>{g.current_price ?? "—"}</td>
            </tr>
          );
        })}
        {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#9E978E]">No completed gifts in this period.</td></tr>}
      </tbody>
    </table>
  );
}

export default function AdminGiftReport() {
  const [granularity, setGranularity] = useState("week");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/admin/gifts/report?granularity=${granularity}&periods=12`).then((r) => setData(r.data)); }, [granularity]);
  const t = data?.totals;
  return (
    <div className="space-y-6" data-testid="admin-gift-report">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="text-xs text-[#9E978E]">From the credit ledger: completed gift transactions, recipient allocations, DateBid retained share and refunds.</p></div>
        <div className="flex rounded-full border border-[#D4AF37]/30 p-1">
          {["week", "month"].map((g) => (
            <button key={g} data-testid={`gift-report-${g}-btn`} onClick={() => setGranularity(g)}
              className={`px-4 py-1.5 rounded-full text-sm ${granularity === g ? "bg-[#D4AF37] text-[#0C0A0A]" : "text-[#D4AF37]"}`}>{g === "week" ? "Weekly" : "Monthly"}</button>
          ))}
        </div>
      </div>
      {data?.pricing_status === "draft_placeholder" && (
        <div data-testid="gift-report-draft-notice" className="flex items-start gap-2 rounded-xl border border-[#E5A84B]/40 bg-[#E5A84B]/10 p-3 text-sm text-[#E5A84B]">
          <AlertTriangle size={16} className="mt-0.5" /> Gift prices are draft placeholders and have not been approved.
        </div>
      )}
      {!data ? <p className="text-sm text-[#9E978E]">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat label="Gifts sent" value={t.gift_count} testid="gift-report-total-count" />
            <Stat label="Credits spent" value={t.credits_spent} testid="gift-report-total-spent" />
            <Stat label="To recipients" value={t.recipient_credits} testid="gift-report-total-recipient" tone="text-[#6FD79E]" />
            <Stat label="DateBid retained" value={t.datebid_retained_credits} testid="gift-report-total-retained" tone="text-[#D4AF37]" />
            <Stat label="Refunded credits" value={t.refunded_credits} testid="gift-report-total-refunded" />
            <Stat label="Failed / reversed" value={t.failed_or_reversed_gifts} testid="gift-report-total-failed" />
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-5"><h3 className="font-serif text-lg text-[#FBF8F3] mb-2">Popularity</h3><PopularityTable rows={data.popularity} /></div>
          <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-5" data-testid="gift-report-series">
            <h3 className="font-serif text-lg text-[#FBF8F3] mb-2">By {granularity}</h3>
            <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#9E978E]"><th className="py-2">Period</th><th>Gifts</th><th>Spent</th><th>To recipients</th><th>Retained</th><th>Refunded</th></tr></thead>
              <tbody>{data.series.map((r) => (
                <tr key={r.period} className="border-t border-white/8 text-[#D8D2C9]" data-testid={`gift-report-period-${r.period}`}><td className="py-2">{r.period}</td><td>{r.gift_count}</td><td>{r.credits_spent}</td><td>{r.recipient_credits}</td><td>{r.datebid_retained_credits}</td><td>{r.refunded_credits}</td></tr>
              ))}{data.series.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#9E978E]">No gift activity.</td></tr>}</tbody></table>
          </div>
        </>
      )}
    </div>
  );
}
